'use client';

import React, { useState } from 'react';
import { Search, UserCheck, Bell, BellOff, X, Check, Users } from 'lucide-react';
import Modal from '@/components/ui/Modal';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  initials: string;
  color: string;
  activeProjects: number;
}

const TEAM_MEMBERS: TeamMember[] = [
  { id: 'tm-001', name: 'Kavya Sharma', role: 'Senior Project Manager', initials: 'KS', color: 'bg-violet-500', activeProjects: 3 },
  { id: 'tm-002', name: 'Daniel Reeves', role: 'Project Manager', initials: 'DR', color: 'bg-blue-500', activeProjects: 2 },
  { id: 'tm-003', name: 'Anika Patel', role: 'Lead Developer', initials: 'AP', color: 'bg-emerald-500', activeProjects: 2 },
  { id: 'tm-004', name: 'Marcus Delgado', role: 'Backend Engineer', initials: 'MD', color: 'bg-orange-500', activeProjects: 1 },
  { id: 'tm-005', name: 'Sofia Navarro', role: 'Digital Marketing Lead', initials: 'SN', color: 'bg-pink-500', activeProjects: 1 },
  { id: 'tm-006', name: 'Rajan Mehta', role: 'Solutions Architect', initials: 'RM', color: 'bg-cyan-500', activeProjects: 2 },
  { id: 'tm-007', name: 'Priya Nambiar', role: 'Full Stack Developer', initials: 'PN', color: 'bg-amber-500', activeProjects: 1 },
  { id: 'tm-008', name: 'Aisha Okonkwo', role: 'BPO Operations Manager', initials: 'AO', color: 'bg-teal-500', activeProjects: 1 },
  { id: 'tm-009', name: 'James Whitfield', role: 'QA Engineer', initials: 'JW', color: 'bg-indigo-500', activeProjects: 0 },
  { id: 'tm-010', name: 'Lena Fischer', role: 'UI/UX Designer', initials: 'LF', color: 'bg-rose-500', activeProjects: 0 },
];

interface BulkAssignModalProps {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  selectedProjectNames: string[];
  onAssign: (memberIds: string[], memberNames: string[], notify: boolean) => void;
}

export default function BulkAssignModal({
  open,
  onClose,
  selectedCount,
  selectedProjectNames,
  onAssign,
}: BulkAssignModalProps) {
  const [search, setSearch] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [notify, setNotify] = useState(true);
  const [step, setStep] = useState<'select' | 'confirm'>('select');

  const filtered = TEAM_MEMBERS.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.role.toLowerCase().includes(search.toLowerCase())
  );

  const toggleMember = (id: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    setSearch('');
    setSelectedMembers(new Set());
    setNotify(true);
    setStep('select');
    onClose();
  };

  const handleAssign = () => {
    const ids = Array.from(selectedMembers);
    const names = TEAM_MEMBERS.filter((m) => selectedMembers.has(m.id)).map((m) => m.name);
    onAssign(ids, names, notify);
    handleClose();
  };

  const selectedMemberList = TEAM_MEMBERS.filter((m) => selectedMembers.has(m.id));

  return (
    <Modal open={open} onClose={handleClose} title="Assign Projects to Team Members" size="md">
      {step === 'select' ? (
        <div className="flex flex-col gap-4">
          {/* Context banner */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/8 border border-primary/20">
            <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Users size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {selectedCount} project{selectedCount > 1 ? 's' : ''} selected for assignment
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                {selectedProjectNames.slice(0, 3).join(', ')}
                {selectedProjectNames.length > 3 && ` +${selectedProjectNames.length - 3} more`}
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search team members…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            />
          </div>

          {/* Team member list */}
          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto scrollbar-thin pr-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No team members found</p>
            ) : (
              filtered.map((member) => {
                const isSelected = selectedMembers.has(member.id);
                return (
                  <button
                    key={member.id}
                    onClick={() => toggleMember(member.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'border-primary/40 bg-primary/8' :'border-transparent hover:bg-muted/50 hover:border-border'
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full ${member.color} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-xs font-semibold text-white">{member.initials}</span>
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{member.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member.role}</p>
                    </div>
                    {/* Active projects badge */}
                    <span className="text-2xs text-muted-foreground whitespace-nowrap">
                      {member.activeProjects} active
                    </span>
                    {/* Check indicator */}
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected ? 'border-primary bg-primary' : 'border-border bg-transparent'
                      }`}
                    >
                      {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Notification toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center gap-2.5">
              {notify ? (
                <Bell size={15} className="text-primary" />
              ) : (
                <BellOff size={15} className="text-muted-foreground" />
              )}
              <div>
                <p className="text-sm font-medium text-foreground">Send assignment notification</p>
                <p className="text-xs text-muted-foreground">Notify selected members via email</p>
              </div>
            </div>
            <button
              onClick={() => setNotify((v) => !v)}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${
                notify ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
              role="switch"
              aria-checked={notify}
              aria-label="Toggle notification"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  notify ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">
              {selectedMembers.size > 0
                ? `${selectedMembers.size} member${selectedMembers.size > 1 ? 's' : ''} selected`
                : 'Select at least one member'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={selectedMembers.size === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <UserCheck size={14} />
                Review & Assign
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Confirmation step */
        <div className="flex flex-col gap-4">
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Assignment Summary</p>
            <div className="flex flex-col gap-2.5">
              <div className="flex items-start gap-2">
                <span className="text-xs text-muted-foreground w-24 flex-shrink-0 pt-0.5">Projects</span>
                <div className="flex-1">
                  <p className="text-sm text-foreground font-medium">
                    {selectedCount} project{selectedCount > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedProjectNames.slice(0, 3).join(', ')}
                    {selectedProjectNames.length > 3 && ` +${selectedProjectNames.length - 3} more`}
                  </p>
                </div>
              </div>
              <div className="h-px bg-border" />
              <div className="flex items-start gap-2">
                <span className="text-xs text-muted-foreground w-24 flex-shrink-0 pt-0.5">Assignees</span>
                <div className="flex flex-col gap-1.5 flex-1">
                  {selectedMemberList.map((m) => (
                    <div key={m.id} className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full ${m.color} flex items-center justify-center`}>
                        <span className="text-2xs font-semibold text-white">{m.initials}</span>
                      </div>
                      <span className="text-sm text-foreground">{m.name}</span>
                      <span className="text-xs text-muted-foreground">· {m.role}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-px bg-border" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-24 flex-shrink-0">Notification</span>
                <div className="flex items-center gap-1.5">
                  {notify ? (
                    <>
                      <Bell size={13} className="text-primary" />
                      <span className="text-sm text-foreground">Email notification will be sent</span>
                    </>
                  ) : (
                    <>
                      <BellOff size={13} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">No notification</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => setStep('select')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
            >
              <X size={13} />
              Back
            </button>
            <button
              onClick={handleAssign}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all"
            >
              <UserCheck size={14} />
              Confirm Assignment
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
